import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import AuthModal from './AuthModal';
import { useAuth } from '../contexts/AuthContext';
import { useLanguageLearning } from '../contexts/LanguageLearningContext';
import useCorporateOrgs from '../hooks/useCorporateOrgs';
import LanguageSelector from './LanguageSelector';
import MenuLearningLanguagePicker from './MenuLearningLanguagePicker';
import { useTranslation } from 'react-i18next';
import { buildPublicLearningPath, getLinkLearningLanguage } from '../lib/learningRoutes';

function releasePageInteractionLocks() {
  if (typeof document === 'undefined') return;
  document.body.style.pointerEvents = '';
  document.body.style.overflow = '';
  document.documentElement.style.pointerEvents = '';
  document.documentElement.style.overflow = '';
}

function removeStaleHeaderMenuArtifacts() {
  if (typeof document === 'undefined') return;
  document.querySelectorAll('[data-header-menu-root="true"]').forEach((node) => {
    node.remove();
  });
}

const Header = ({ hideLanguageSelector = false }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [pendingAuthMode, setPendingAuthMode] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [menuSwipeOffset, setMenuSwipeOffset] = useState(0);
  const [menuSwipeActive, setMenuSwipeActive] = useState(false);
  const { currentUser, logout } = useAuth();
  const { preferredLanguage } = useLanguageLearning();
  const { orgs: corporateOrgs } = useCorporateOrgs();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const menuTouchRef = useRef({ startX: 0, startY: 0, active: false });
  // Links follow the visitor's chosen learning language, or the page's language
  // when there is no preference (Google, first-time visitors). English public
  // pages link to their unprefixed canonical URLs.
  const learningLanguage = getLinkLearningLanguage(preferredLanguage, location.pathname);
  const lessonsPath = buildPublicLearningPath('/podcast-lessons', learningLanguage);
  const libraryPath = buildPublicLearningPath('/library', learningLanguage);
  const learningPathsPath = buildPublicLearningPath('/learning-paths', learningLanguage);
  const liveClassesPath = buildPublicLearningPath('/live-classes', learningLanguage);
  const methodologyPath = buildPublicLearningPath('/methodology', learningLanguage);
  const coachPath = buildPublicLearningPath('/coach', learningLanguage);
  const dashboardPath = buildPublicLearningPath('/dashboard', learningLanguage);
  const corporateTrainingPath = '/corporate-training';

  // Corporate nav: pick the best org deterministically and route by role.
  // Priority: supervisor > learner. Trainer-only members are excluded because
  // no corporate page currently accepts trainer-only access via CorporateRoute.
  // Ties are broken by orgName then orgId for stability.
  const primaryOrg = (() => {
    const routable = corporateOrgs.filter(
      (o) => o.roles?.includes('supervisor') || o.roles?.includes('learner'),
    );
    if (routable.length === 0) return null;
    routable.sort((a, b) => {
      const aSuper = a.roles.includes('supervisor') ? 0 : 1;
      const bSuper = b.roles.includes('supervisor') ? 0 : 1;
      if (aSuper !== bSuper) return aSuper - bSuper;
      return (a.orgName || a.orgId).localeCompare(b.orgName || b.orgId);
    });
    return routable[0];
  })();
  const corporatePath = primaryOrg
    ? (primaryOrg.roles.includes('supervisor')
      ? `/corporate/dashboard?org=${primaryOrg.orgId}`
      : `/corporate/employee?org=${primaryOrg.orgId}`)
    : null;

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const openAuthModal = (mode) => { setAuthMode(mode); setAuthModalOpen(true); };
  const openAuthFromMenu = (mode) => {
    setPendingAuthMode(mode);
    setIsMenuOpen(false);
  };

  const handleLogout = async () => {
    try { await logout(); navigate('/farewell'); }
    catch (error) { console.error('Failed to log out', error); }
  };

  const menuColors = {
    surface: '#FDFBF7',
    surfaceAlt: '#f5f5f4',
    textPrimary: '#1c1917',
    textSecondary: '#57534e',
    border: '#e7e5e4'
  };
  const isStoreRoute = location.pathname.startsWith('/store');
  const isLibraryRoute = location.pathname.includes('/library');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setAuthModalOpen(false);
    setPendingAuthMode(null);
  }, [location.pathname, currentUser?.uid]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (isMenuOpen || !pendingAuthMode) return;
    openAuthModal(pendingAuthMode);
    setPendingAuthMode(null);
  }, [isMenuOpen, pendingAuthMode]);

  useEffect(() => {
    if (isMenuOpen) return;
    setMenuSwipeOffset(0);
    setMenuSwipeActive(false);
    menuTouchRef.current = { startX: 0, startY: 0, active: false };
  }, [isMenuOpen]);

  useEffect(() => {
    if (isMenuOpen || authModalOpen) return undefined;

    const frameId = window.requestAnimationFrame(() => {
      releasePageInteractionLocks();
      removeStaleHeaderMenuArtifacts();
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [isMenuOpen, authModalOpen, location.pathname, currentUser?.uid]);

  const handleMenuTouchStart = (event) => {
    if (!isMenuOpen || event.touches.length !== 1) return;
    const touch = event.touches[0];
    menuTouchRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      active: true,
    };
    setMenuSwipeActive(true);
  };

  const handleMenuTouchMove = (event) => {
    const touchState = menuTouchRef.current;
    if (!touchState.active || event.touches.length !== 1) return;

    const touch = event.touches[0];
    const deltaX = touch.clientX - touchState.startX;
    const deltaY = touch.clientY - touchState.startY;

    // Track only intentional horizontal swipe-to-close gestures.
    if (deltaX <= 0) {
      setMenuSwipeOffset(0);
      return;
    }
    if (Math.abs(deltaX) < Math.abs(deltaY) * 1.1) return;

    if (event.cancelable) event.preventDefault();
    setMenuSwipeOffset(Math.min(deltaX, 360));
  };

  const handleMenuTouchEnd = () => {
    const shouldClose = menuSwipeOffset > 96;
    menuTouchRef.current.active = false;
    setMenuSwipeActive(false);
    if (shouldClose) {
      setMenuSwipeOffset(0);
      setIsMenuOpen(false);
      return;
    }
    setMenuSwipeOffset(0);
  };

  const backdropOpacity = 0.5 * (1 - Math.min(menuSwipeOffset / 240, 1));

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[2147483647] focus:px-4 focus:py-3 focus:rounded-full focus:bg-[#1c1917] focus:text-white focus:text-sm focus:font-semibold focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#D63AB4] focus:ring-offset-2"
      >
        {t('common:accessibility.skipToContent', 'Skip to main content')}
      </a>
      <header
        className="not-prose sticky top-0 z-[200] h-16 lg:h-[4.5rem] border-b supports-[backdrop-filter]:backdrop-blur-xl transition-all duration-300"
        style={{
          isolation: 'isolate',
          background: scrolled ? 'rgba(253, 251, 247, 0.9)' : 'transparent',
          boxShadow: 'none',
          borderBottom: scrolled ? '1px solid rgba(214,211,209,0.8)' : '1px solid transparent',
          backdropFilter: scrolled ? 'blur(10px)' : 'none',
          WebkitBackdropFilter: scrolled ? 'blur(10px)' : 'none',
        }}
      >
        <div className="mx-auto h-full max-w-[1440px] px-5 sm:px-8 lg:px-10">
          <div className="flex h-full items-center justify-between gap-4 lg:gap-10">
            {/* Logo */}
            <div className="flex shrink-0 items-center">
              <Link to="/" className="flex items-center">
                <div className="flex flex-col items-start justify-center leading-none">
                  <span
                    className="text-[1.95rem] font-bold leading-none tracking-[-0.05em] text-stone-900 sm:text-[2.1rem] lg:text-[2.35rem]"
                    style={{ fontFamily: 'var(--font-display)' }}
                  >
                    Aprendos
                  </span>
                </div>
              </Link>
            </div>

            {/* Centre Navigation (desktop) */}
            <div className="hidden flex-1 justify-center lg:flex">
              <div
                className={`flex items-center gap-4 xl:gap-7 ${isStoreRoute ? 'justify-center w-full' : ''}`}
              >
                {currentUser && !isStoreRoute && (
                  <Link
                    to="/store"
                    className="py-2 text-sm font-semibold text-stone-600 transition-colors hover:text-[#9d1c7f]"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('common:navigation.store', 'Store')}
                  </Link>
                )}
                {currentUser && !isLibraryRoute && (
                  <Link
                    to={libraryPath}
                    className="py-2 text-sm font-semibold text-stone-600 transition-colors hover:text-[#9d1c7f]"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('common:navigation.library', 'Library')}
                  </Link>
                )}
                {currentUser && corporatePath && (
                  <Link
                    to={corporatePath}
                    className="py-2 text-sm font-semibold text-[#B2288E] transition-colors hover:text-[#9d1c7f]"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('common:navigation.myCorporate', 'My Corporate')}
                  </Link>
                )}
                {!corporatePath && (
                  <Link
                    to={corporateTrainingPath}
                    className="py-2 text-sm font-semibold text-stone-600 transition-colors hover:text-[#9d1c7f]"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('common:navigation.corporate', 'Corporate')}
                  </Link>
                )}
                <Link
                  to={learningPathsPath}
                  className="py-2 text-sm font-semibold text-stone-800 transition-colors hover:text-[#9d1c7f]"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('common:navigation.memberships', 'Memberships')}
                </Link>
                {!corporatePath && (
                  <Link
                    to={liveClassesPath}
                    className="py-2 text-sm font-semibold text-stone-800 transition-colors hover:text-[#9d1c7f]"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('common:navigation.liveClasses', 'Live Classes')}
                  </Link>
                )}
                <div className="ml-3 inline-flex items-center justify-center group xl:ml-4">
                  <Link
                    to={lessonsPath}
                    className="inline-flex items-center justify-center rounded-full bg-[#1c1917] px-5 py-2 text-sm font-semibold text-white shadow-md transition-transform duration-200 hover:-translate-y-0.5 whitespace-nowrap"
                    aria-label={t('common:navigation.browseLessons')}
                  >
                    {t('common:navigation.browseLessons')}
                    <svg
                      aria-hidden="true"
                      viewBox="0 0 10 10"
                      height="12"
                      width="12"
                      fill="none"
                      className="ml-2 stroke-white stroke-2"
                    >
                      <path d="M0 5h7" className="transition-opacity opacity-0 group-hover:opacity-100" />
                      <path d="M1 1l4 4-4 4" className="transition-transform group-hover:translate-x-[3px]" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>

            {/* Right Side (your exact Menu button) */}
            <div className="flex shrink-0 items-center gap-3 sm:gap-4 lg:gap-6">
              {!currentUser && (
                <div className="hidden lg:flex items-center gap-2.5 xl:gap-3">
                  <motion.button
                    type="button"
                    whileHover={{ y: -1 }}
                    whileTap={{ scale: 0.98 }}
                    className="text-sm font-semibold text-stone-700 transition-colors hover:text-[#B2288E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D63AB4] focus-visible:ring-offset-2"
                    style={{ fontFamily: 'var(--font-body)' }}
                    onClick={() => openAuthModal('login')}
                  >
                    {t('common:buttons.login')}
                  </motion.button>
                </div>
              )}
              <div className="ml-1 flex items-center gap-3 pl-1 sm:ml-2 sm:gap-4 sm:pl-2 lg:ml-6 lg:gap-5 lg:pl-0">
                {!hideLanguageSelector && (
                  <div className="text-[#D63AB4]">
                    <LanguageSelector />
                  </div>
                )}

                <button
                  type="button"
                  onClick={toggleMenu}
                  aria-expanded={isMenuOpen ? "true" : "false"}
                  aria-label={t('common:navigation.toggleMenu', 'Toggle navigation menu')}
                  className="text-sm font-medium text-[#1c1917] transition-colors hover:text-[#B2288E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D63AB4] focus-visible:ring-offset-2 rounded-sm"
                >
                  {t('common:navigation.menu', 'Menu')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Premium Navigation Menu Overlay */}
      {isMenuOpen && typeof document !== 'undefined' && createPortal(
        <div data-header-menu-root="true">
          <div
            className="premium-menu-backdrop fixed inset-0 z-[12000]"
            onClick={() => setIsMenuOpen(false)}
            style={{ zIndex: 2147483000, backgroundColor: `rgba(0, 0, 0, ${backdropOpacity})`, backdropFilter: 'blur(2px)' }}
          />

          <div
            className="premium-menu-panel fixed right-0 top-0 z-[12010] h-full w-full max-w-md shadow-2xl overflow-y-auto"
            onTouchStart={handleMenuTouchStart}
            onTouchMove={handleMenuTouchMove}
            onTouchEnd={handleMenuTouchEnd}
            onTouchCancel={handleMenuTouchEnd}
            style={{
              zIndex: 2147483010,
              background: menuColors.surface,
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
              borderLeft: `1px solid ${menuColors.border}`,
              transform: `translateX(${menuSwipeOffset}px)`,
              transition: menuSwipeActive ? 'none' : 'transform 220ms cubic-bezier(0.22, 1, 0.36, 1)',
              willChange: 'transform',
              touchAction: 'pan-y',
            }}
          >
            <div
              className="premium-menu-content"
              style={{ color: menuColors.textPrimary }}
            >
              <div
                className="premium-menu-header sticky top-0 z-10 px-4 py-3"
                style={{
                  borderBottom: `1px solid ${menuColors.border}`,
                  background: menuColors.surface
                }}
              >
                <div className="flex items-center">
                  <span
                    className="font-semibold leading-none tracking-[-0.05em]"
                    style={{
                      color: menuColors.textPrimary,
                      fontSize: 'clamp(1.9rem, 4vw, 2.5rem)',
                      fontFamily: 'var(--font-display)',
                    }}
                  >
                    Aprendos
                  </span>
                </div>
                <p className="text-sm mt-2" style={{ color: menuColors.textSecondary }}>
                  Finally...<br />
                  language learning that{' '}
                  <span className="relative inline-block whitespace-nowrap">
                    <span className="relative z-10 italic" style={{ color: '#B2288E' }}>makes sense</span>
                    <svg className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-[75%]" height="6" viewBox="0 0 100 6" fill="none" preserveAspectRatio="none">
                      <path d="M2 4 Q 50 1, 98 4" stroke="#B2288E" strokeWidth="2.5" strokeLinecap="round" fill="none" />
                    </svg>
                  </span>
                </p>
              </div>

              <nav className="premium-menu-nav px-4 py-4">
                {/* Learning Language selector - self-contained editorial picker */}
                {!hideLanguageSelector && (
                  <div className="mb-6 px-4 pt-2 pb-4">
                    <MenuLearningLanguagePicker onAfterSelect={() => setIsMenuOpen(false)} />
                  </div>
                )}

                {/* Primary navigation */}
                {[
                  { to: methodologyPath, title: t('common:navigation.methodology'), desc: t('pages:methodology.subtitle') },
                  { to: lessonsPath, title: t('common:navigation.lessons'), desc: t('pages:podcastLessons.subtitle', 'Browse all podcast lessons') },
                  {
                    to: learningPathsPath,
                    title: t('common:navigation.memberships', 'Memberships'),
                    desc: t('common:navigation.menuItems.memberships.description', 'Plans and subscriptions')
                  },
                  !corporatePath && {
                    to: liveClassesPath,
                    title: t('common:navigation.liveClasses', 'Live Classes'),
                    desc: t('common:navigation.menuItems.liveClasses.description', 'Join our live conversation classes')
                  },
                  {
                    to: '/book',
                    title: t('common:navigation.bookClass', 'Book Class'),
                    desc: t('common:navigation.menuItems.bookClass.description', 'Book a 1:1 session with a trainer')
                  },
                ].filter(Boolean).map(link => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="premium-menu-link group flex items-start justify-between p-4 rounded-lg transition-all duration-200 hover:bg-[#f5f5f4] hover:translate-x-1"
                    onClick={() => setIsMenuOpen(false)}
                    style={{ color: menuColors.textPrimary, background: 'transparent' }}
                  >
                    <div>
                      <span className="premium-menu-link-title font-medium" style={{ color: menuColors.textPrimary }}>
                        {link.title}
                      </span>
                      <span className="premium-menu-link-desc block text-sm mt-1" style={{ color: menuColors.textSecondary }}>
                        {link.desc}
                      </span>
                    </div>
                    <div className="premium-menu-link-arrow text-xl transition-transform duration-200 group-hover:translate-x-1" style={{ color: '#D63AB4' }}>{'\u2192'}</div>
                  </Link>
                ))}

                {/* Secondary navigation */}
                {[
                  {
                    to: "/store",
                    titleKey: "store",
                    title: t('common:navigation.store', 'Store'),
                    desc: t('common:navigation.menuItems.store.description', 'Shop premium learning materials')
                  },
                  {
                    to: libraryPath,
                    titleKey: "library",
                    title: t('common:navigation.library', 'Library'),
                    desc: t('common:navigation.menuItems.library.description', 'Access your reading collections')
                  },
                  { to: buildPublicLearningPath('/meet-creator', learningLanguage), titleKey: "meetCreator", title: t('common:navigation.menuItems.meetCreator.title'), desc: t('common:navigation.menuItems.meetCreator.description') },
                  // TODO: Re-enable when ready
                  // { to: "/meet-the-hosts", titleKey: "meetTheHosts", title: t('common:navigation.menuItems.meetTheHosts.title'), desc: t('common:navigation.menuItems.meetTheHosts.description') },
                  { to: "/choose-language?intent=placement-test", titleKey: "placementTest", title: t('common:navigation.menuItems.placementTest.title'), desc: t('common:navigation.menuItems.placementTest.description') },
                  { to: coachPath, titleKey: "coach", title: t('common:navigation.menuItems.coach.title', 'Coach'), desc: t('common:navigation.menuItems.coach.description', 'Personalized practice recommendations') },
                  // TODO: Re-enable when ready
                  // { to: "/community", titleKey: "community", title: t('common:navigation.menuItems.community.title'), desc: t('common:navigation.menuItems.community.description') },
                ].map(link => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="premium-menu-link group flex items-start justify-between p-4 rounded-lg transition-all duration-200 hover:bg-[#f5f5f4] hover:translate-x-1"
                    onClick={() => setIsMenuOpen(false)}
                    style={{ color: menuColors.textPrimary, background: 'transparent' }}
                  >
                    <div>
                      <span className="premium-menu-link-title font-medium" style={{ color: menuColors.textPrimary }}>
                        {link.title}
                      </span>
                      <span className="premium-menu-link-desc block text-sm mt-1" style={{ color: menuColors.textSecondary }}>
                        {link.desc}
                      </span>
                    </div>
                    <div className="premium-menu-link-arrow text-xl transition-transform duration-200 group-hover:translate-x-1" style={{ color: '#D63AB4' }}>{'\u2192'}</div>
                  </Link>
                ))}

                {/* Corporate entry */}
                <div className="mt-2 pt-2" style={{ borderTop: `1px solid ${menuColors.border}` }}>
                  <Link
                    to={corporatePath || corporateTrainingPath}
                    className="premium-menu-link group flex items-start justify-between p-4 rounded-lg transition-all duration-200 hover:bg-[#f5f5f4] hover:translate-x-1"
                    onClick={() => setIsMenuOpen(false)}
                    style={{ color: menuColors.textPrimary, background: 'transparent' }}
                  >
                    <div>
                      <span className="premium-menu-link-title font-medium" style={{ color: '#B2288E' }}>
                        {corporatePath
                          ? t('common:navigation.myCorporate', 'My Corporate')
                          : t('common:navigation.corporate', 'Corporate')}
                      </span>
                      <span className="premium-menu-link-desc block text-sm mt-1" style={{ color: menuColors.textSecondary }}>
                        {corporatePath
                          ? (primaryOrg?.orgName || t('common:navigation.corporateDesc', 'Your corporate learning area'))
                          : t('common:navigation.corporateDesc', 'Corporate language training')}
                      </span>
                    </div>
                    <div className="premium-menu-link-arrow text-xl transition-transform duration-200 group-hover:translate-x-1" style={{ color: '#D63AB4' }}>{'\u2192'}</div>
                  </Link>
                </div>
              </nav>

              {!currentUser && (
                <div className="premium-menu-auth pb-8 px-4">
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button
                      variant="outline"
                      className="w-full mb-3 transition-all duration-200 hover:border-[#D63AB4] hover:text-[#D63AB4]"
                      style={{ borderColor: '#d6d3d1', color: '#1c1917', background: '#FFFFFF' }}
                      onClick={() => openAuthFromMenu('login')}
                    >
                      {t('common:buttons.login')}
                    </Button>
                  </motion.div>
                  <motion.div whileHover={{ scale: 1.02, y: -2 }} whileTap={{ scale: 0.98 }}>
                    <Button
                      className="w-full transition-all duration-200 hover:shadow-lg"
                      style={{ background: '#1c1917', color: '#FFFFFF' }}
                      onClick={() => openAuthFromMenu('signup')}
                    >
                      {t('common:buttons.signup')}
                    </Button>
                  </motion.div>
                </div>
              )}
              {currentUser && (
                <div className="premium-menu-auth px-4 pb-8">
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Link to={dashboardPath} className="w-full mb-3 block" onClick={() => setIsMenuOpen(false)}>
                      <Button
                        variant="outline"
                        className="w-full transition-all duration-200 hover:border-[#D63AB4] hover:text-[#D63AB4]"
                        style={{ borderColor: '#d6d3d1', color: '#1c1917', background: '#FFFFFF' }}
                      >
                        {t('common:navigation.dashboard')}
                      </Button>
                    </Link>
                  </motion.div>
                  <motion.div whileHover={{ scale: 1.02, y: -2 }} whileTap={{ scale: 0.98 }}>
                    <Button
                      onClick={() => { handleLogout(); setIsMenuOpen(false); }}
                      className="w-full transition-all duration-200 hover:shadow-lg"
                      style={{ background: '#1c1917', color: '#FFFFFF' }}
                    >
                      {t('common:buttons.logout')}
                    </Button>
                  </motion.div>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        mode={authMode}
      />
    </>
  );
};

export default Header;
